/**
 * Поверхность разработчика — вторая половина разреза (docs/UI_PLAN.md §2).
 *
 * ПРАВИЛО РАЗРЕЗА. К каждой строке задан один вопрос: изменит ли она решение ехать? Нет —
 * значит сюда. Ничего не удалено из продукта: сюда переехало то, что адресовано НАМ, а не
 * то, что длинное. Журнал отклонённых дат уезжает потому, что написан для нас; будь он
 * написан для копателя, его бы сократили, а не спрятали.
 *
 * ГДЕ ОНА ЖИВЁТ. Подвал консоли целиком становится ручкой: `.k-console` — flex-колонка с
 * прокручиваемым телом, и подвал это ЕДИНСТВЕННАЯ её полоса, видимая всегда, без прокрутки.
 * «Кнопка внизу» в вебе обычно значит «кнопка, до которой надо долистать»; здесь не значит.
 * Бейдж флагов показанного прогона остаётся на ручке видимым и в закрытом состоянии —
 * требование брифа про провенанс выполняется точнее, чем раньше: ноль кликов до провенанса,
 * один до переключателей.
 *
 * ПОЧЕМУ НЕ МОДАЛКА. Ящик нужен, чтобы СВЕРЯТЬ экран с тем, что сказал пайплайн; окно,
 * закрывающее полосу состояния и вердикт, ломает ровно ту работу, ради которой его открыли.
 * Поэтому раскрытие вверх, на месте, внутри колонки консоли, со своей прокруткой.
 */

import type { ReactNode } from "react";

import { T } from "../strings";
import type { Field, FieldUse as FieldUseData, FlagNote, Run, RunFlags } from "../api/types";
import { formatDate } from "../lib/format";
import { radarAnsweredThisRun } from "../lib/radarSeries";
import type { StripModel } from "../lib/strip";
import type { Verdict } from "../lib/verdict";
import type { QueueEntry, RunState } from "../state/useWorkspace";
import { BatchQueue } from "./BatchQueue";
import { Evidence } from "./Evidence";
import { FieldUse } from "./FieldUse";
import { FlagsBadge } from "./FlagsBadge";
import { RadarSeries } from "./RadarSeries";
import { RejectedLog } from "./RejectedLog";
import { RunProgress } from "./RunProgress";

interface Props {
  open: boolean;
  onToggle: () => void;
  field: Field | null;
  activeRun: Run | null;
  /** Полная таблица шагов живёт ЗДЕСЬ: на продуктовой поверхности прогон говорит кнопкой. */
  runState: RunState;
  verdict: Verdict | null;
  strip: StripModel | null;
  busy: boolean;
  flags: RunFlags;
  onFlagsChange: (flags: RunFlags) => void;
  analysisDate: string;
  onAnalysisDate: (date: string) => void;
  checkedCount: number;
  onRunBatch: () => void;
  queue: QueueEntry[];
  onClearQueue: () => void;
  onSelectField: (fieldId: number) => void;
  fieldUse: FieldUseData | null;
  /** Ворота печати живут в режиме верификации; здесь — только продуктовый вход в сверку. */
  verificationMode: boolean;
  /**
   * Печать закрывает ЧИСЛА, а не только заголовок. Журнал отклонённых дат и доказательства —
   * такой же канал вердикта, и открыть их из ящика значило бы обойти собственный замер: он
   * тогда измерял бы нашу же подсказку.
   */
  sealed: boolean;
  groundTruthCount: number;
  onOpenObservation: () => void;
}

/**
 * Раздел панели. Подпись обязательна, а не опциональна: «Провенанс» и «Сверка» — это два
 * слова, по которым не догадаться, что за ними лежит, и владелец спросил ровно это («что
 * это вообще такое?»). Одна строка под заголовком стоит дешевле, чем орган, к которому
 * никто не притрагивается.
 */
const Section = ({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: ReactNode;
}) => (
  <section className="k-dev__section">
    <div className="k-label k-dev__head">{title}</div>
    <p className="k-hint k-dev__note">{note}</p>
    {children}
  </section>
);

/**
 * Тумблер флага с объяснением РЯДОМ, а не в `title`.
 *
 * Три строки, и каждая отвечает на свой вопрос: что он делает сейчас, что будет, если его
 * перекинуть, и что означает положение по умолчанию. Третья — самая важная и единственная,
 * которой раньше не было нигде, кроме `.env.example`: оба флага существуют только затем,
 * чтобы воспроизвести поведение ноутбука и сравниться с ним. Без этой фразы «legacy»
 * читается как «хуже», и тумблер зовёт себя выключить «чтобы стало правильно».
 */
const FlagToggle = ({
  name,
  on,
  now,
  otherwise,
  meaning,
  scope,
  onToggle,
}: {
  name: string;
  on: boolean;
  /** Что флаг делает в нынешнем положении. */
  now: string;
  /** Что изменится, если его перекинуть. */
  otherwise: string;
  /** Что означает умолчание — и почему флаг вообще есть. */
  meaning: string;
  /** Граница действия флага, когда она не очевидна. */
  scope?: string;
  onToggle: () => void;
}) => (
  <div className="k-dev__flag">
    <button type="button" className="k-btn k-btn--compact" aria-pressed={on} onClick={onToggle}>
      {name} {on ? T.flags.on : T.flags.off}
    </button>
    <p className="k-hint k-dev__note">{now}</p>
    <p className="k-hint k-dev__note">
      {on ? T.flags.ifOff : T.flags.ifOn} {otherwise}
    </p>
    {scope && <p className="k-hint k-dev__note">{scope}</p>}
    <p className="k-hint k-dev__note k-dev__note--why">{meaning}</p>
  </div>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <>
    <dt>{label}</dt>
    <dd>{value}</dd>
  </>
);

/** Оговорки прогона: канал есть, кодов сейчас ноль. Следующий сядет сюда без обвязки. */
const noteText = (note: FlagNote): string => T.notes.unknown(note.code);

export function DevPanel({
  open,
  onToggle,
  field,
  activeRun,
  runState,
  verdict,
  strip,
  busy,
  flags,
  onFlagsChange,
  analysisDate,
  onAnalysisDate,
  checkedCount,
  onRunBatch,
  queue,
  onClearQueue,
  onSelectField,
  fieldUse,
  verificationMode,
  sealed,
  groundTruthCount,
  onOpenObservation,
}: Props) {
  const payload = activeRun?.payload ?? null;
  const optical = payload?.optical_state ?? null;
  const notes = payload?.notes ?? [];
  const mainAnswer = payload?.result?.main_answer;
  const why = payload?.result?.["why"];
  // Ряд радара, который НИЧЕГО не решил, — прибор, и его адрес здесь. Когда ответ стоит на
  // нём, он доказательство и живёт в части 2, рядом с вердиктом: копия здесь была бы вторым
  // экземпляром одного графика на одном экране. `RadarSeries` сама возвращает null, когда
  // ряда нет вовсе, — на обычном прогоне слова «радар» не появляется и тут.
  const radarDecided = radarAnsweredThisRun(activeRun);

  return (
    <>
      {open && (
        <div className="k-dev k-scroll" data-testid="dev-panel">
          {/* Первое, что читает открывший ящик. Набор тумблеров и таблиц без этой строки
              выглядит настройками продукта, и вопрос «зачем мне это включать» не имеет
              ответа на экране. */}
          <p className="k-hint k-dev__about" data-testid="dev-about">
            {T.dev.about}
          </p>

          {/* Порядок разделов — порядок отладки прогона: чем прогнали → что выбросили →
              какие числа получились → чем это подписано → чем сверяется. */}
          <Section title={T.dev.sections.run} note={T.dev.sectionNotes.run}>
            <div className="k-row k-row--wrap">
              <label className="k-field" style={{ flex: "0 0 9.5rem" }}>
                <span className="k-label">{T.run.analysisDate}</span>
                <input
                  className="k-input"
                  type="date"
                  value={analysisDate}
                  onChange={(event) => onAnalysisDate(event.target.value)}
                />
              </label>
              {checkedCount > 1 && (
                <button type="button" className="k-btn" disabled={busy} onClick={onRunBatch}>
                  {T.run.startBatch(checkedCount)}
                </button>
              )}
            </div>
            <p className="k-hint k-dev__note">{T.run.analysisDateNote}</p>
            {checkedCount > 1 && <p className="k-hint k-dev__note">{T.run.startBatchNote}</p>}

            {/* Флаги СЛЕДУЮЩЕГО прогона. Провенанс показанного — бейдж на ручке ниже, и
                подписаны эти две вещи по-разному сознательно.
                Оба флага остаются переключаемыми руками, и это требование брифа, а не
                инерция: «возможность перезапустить поле с другими флагами — сравнение
                вариантов и есть смысл этапа» (визуальный контракт). Read-only здесь отняло бы
                у этапа его работу; чего им не хватало — не запрета, а объяснения. */}
            <div className="k-dev__flags">
              <span className="k-label">{T.flags.title}</span>
              <p className="k-hint k-dev__note">{T.flags.scope}</p>
              <FlagToggle
                name={T.flags.legacyGrid}
                on={flags.QA_LEGACY_GRID}
                now={flags.QA_LEGACY_GRID ? T.flags.legacyGridOn : T.flags.legacyGridOff}
                otherwise={flags.QA_LEGACY_GRID ? T.flags.legacyGridOff : T.flags.legacyGridOn}
                meaning={T.flags.legacyGridDefault}
                onToggle={() => onFlagsChange({ ...flags, QA_LEGACY_GRID: !flags.QA_LEGACY_GRID })}
              />
              <FlagToggle
                name={T.flags.powerDomain}
                on={flags.RADAR_POWER_DOMAIN}
                now={flags.RADAR_POWER_DOMAIN ? T.flags.powerDomainOn : T.flags.powerDomainOff}
                otherwise={
                  flags.RADAR_POWER_DOMAIN ? T.flags.powerDomainOff : T.flags.powerDomainOn
                }
                scope={T.flags.powerDomainScope}
                meaning={T.flags.powerDomainDefault}
                onToggle={() =>
                  onFlagsChange({ ...flags, RADAR_POWER_DOMAIN: !flags.RADAR_POWER_DOMAIN })
                }
              />
            </div>

            {/* ПОЛНАЯ таблица шагов живёт здесь и только здесь (UX_SPEC §5, §12): на
                продуктовой поверхности идущий прогон произносит одна кнопка, а построчный
                разбор нужен тому, кто прогон отлаживает. */}
            <RunProgress runState={runState} />

            <BatchQueue queue={queue} onClear={onClearQueue} onSelect={onSelectField} />
          </Section>

          {sealed ? (
            <Section title={T.dev.sections.dates} note={T.dev.sectionNotes.dates}>
              <p className="k-caption" data-testid="dev-sealed">
                {T.dev.sealed}
              </p>
            </Section>
          ) : (
            <>
              <Section title={T.dev.sections.dates} note={T.dev.sectionNotes.dates}>
                {strip ? <RejectedLog model={strip} /> : <p className="k-caption">{T.dev.noRun}</p>}
              </Section>

              <Section title={T.dev.sections.numbers} note={T.dev.sectionNotes.numbers}>
                {/* Заголовки блоков колонки — русские ответы. Сырые коды, которые их
                    породили, не показывались нигде: сверить экран с payload по русской
                    фразе нельзя, а сверка — единственная работа этой панели. */}
                <div className="k-label" style={{ marginTop: "var(--gap-2)" }}>
                  {T.dev.blockCodes}
                </div>
                <dl className="k-readout" data-testid="dev-block-codes">
                  <Row label={T.dev.blockUse} value={fieldUse?.field_use_code ?? T.common.noData} />
                  <Row
                    label={T.dev.blockVeg}
                    value={optical?.vegetation_state_class ?? T.common.noData}
                  />
                  <Row
                    label={T.dev.blockPosition}
                    value={optical?.local_position_code ?? T.common.noData}
                  />
                  <Row
                    label={T.dev.blockConfidence}
                    value={optical?.confidence ?? T.common.noData}
                  />
                  <Row
                    label={T.dev.blockAnswer}
                    value={activeRun?.result?.decision ?? T.common.noData}
                  />
                </dl>

                {activeRun ? (
                  <Evidence run={activeRun} open />
                ) : (
                  <p className="k-caption">{T.dev.noRun}</p>
                )}
                {field === null ? (
                  <p className="k-caption">{T.dev.noField}</p>
                ) : (
                  <FieldUse fieldUse={fieldUse} />
                )}
                {!radarDecided && <RadarSeries run={activeRun} dev />}
              </Section>
            </>
          )}

          {!sealed && (
            <Section title={T.dev.sections.provenance} note={T.dev.sectionNotes.provenance}>
              {activeRun === null ? (
                <p className="k-caption">{T.dev.noRun}</p>
              ) : (
                <>
                  <dl className="k-readout">
                    <Row label={T.dev.runStatus} value={activeRun.status} />
                    <Row label={T.run.analysisDate} value={formatDate(activeRun.analysis_date)} />
                    <Row
                      label={T.run.geeCalls}
                      value={
                        activeRun.gee_calls === null ? T.common.noData : String(activeRun.gee_calls)
                      }
                    />
                    <Row
                      label={T.dev.codeVersion}
                      value={activeRun.code_version ?? T.common.noData}
                    />
                    {verdict?.opticalAnswer && (
                      <Row label={T.dev.opticalAnswer} value={verdict.opticalAnswer} />
                    )}
                    {payload?.radar_skip_reason && (
                      <Row label={T.dev.radarSkip} value={payload.radar_skip_reason} />
                    )}
                  </dl>

                  {/* Единственный адрес балла на экране, и определение стоит рядом с числом.
                    На продуктовой поверхности его нет: величина равна шагу сигнала,
                    делённому на его же порог, — напечатать её значит напечатать порог, а
                    прочитать её правильно всё равно нельзя (правила текстов — ТЗ.md). */}
                  {verdict !== null && verdict.score !== null && (
                    <p className="k-caption" data-testid="dev-score">
                      {T.dev.score}: {verdict.score} — {T.dev.scoreWhat}
                    </p>
                  )}

                  {activeRun.error && (
                    <p className="k-note k-note--error">
                      {T.dev.runError}: {activeRun.error}
                    </p>
                  )}

                  {notes.map((note) => (
                    <p className="k-note k-note--warn" key={note.code}>
                      {noteText(note)}
                    </p>
                  ))}

                  {verdict !== null && verdict.warnings.length > 0 && (
                    <div className="k-note k-note--warn">
                      <div className="k-label">{T.verdict.warnings}</div>
                      <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                        {verdict.warnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Проза пайплайна дословно. Она НЕ называет состояние — состояние называет
                    только strings.ts, иначе заголовок говорит «ОСВОБОДИЛОСЬ», а строка под
                    ним «ПОЛЕ УБРАНО между …»: два словаря на один ответ. */}
                  {typeof mainAnswer === "string" && (
                    <p className="k-caption" data-testid="dev-main-answer">
                      {T.dev.mainAnswer}: {mainAnswer}
                    </p>
                  )}
                  {typeof why === "string" && (
                    <p className="k-caption" data-testid="dev-why">
                      {T.dev.why}: {why}
                    </p>
                  )}
                </>
              )}
            </Section>
          )}

          {/* Ввод наблюдения. В режиме верификации он ворота и стоит на продуктовой
              поверхности; вне режима — обычный вход оператора, и его место здесь. Экран
              сверки удалён 2026-09-05 (решение владельца) — раздел остаётся единственным
              входом записи наблюдений, а цифра владельца — CSV /api/truth/agreement.csv. */}
          {!verificationMode && (
            <Section title={T.dev.sections.reconcile} note={T.dev.sectionNotes.reconcile}>
              <button
                type="button"
                className="k-btn k-btn--ghost k-btn--block"
                disabled={field === null}
                onClick={onOpenObservation}
              >
                {T.groundTruth.open}
              </button>
              {groundTruthCount > 0 && (
                <p className="k-hint">{T.groundTruth.existing(groundTruthCount)}</p>
              )}
            </Section>
          )}
        </div>
      )}

      <button
        type="button"
        className={`k-console__foot k-devfoot ${open ? "k-devfoot--open" : ""}`}
        aria-expanded={open}
        title={open ? T.dev.close : T.dev.open}
        onClick={onToggle}
        data-testid="dev-toggle"
      >
        <span className="k-devfoot__chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <span className="k-hint">{T.dev.title}</span>
        <span className="k-mono k-devfoot__key">{T.dev.hint}</span>
        <span className="k-spacer" />
        {/* Провенанс ПОКАЗАННОГО вердикта, не «флаги следующего прогона»: иначе оператор,
            сравнивающий варианты, прочтёт отложенные переключатели как флаги на экране. */}
        <span className="k-hint">{activeRun ? T.flags.ofRun : T.flags.title}</span>
        <FlagsBadge flags={activeRun?.flags ?? flags} />
      </button>
    </>
  );
}
